import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
//@ts-ignore
import { MindARThree } from 'mind-ar/dist/mindar-image-three.prod.js';

type TargetConfig = {
  index: number;
  name: string;
  modelUrl: string;
  occluderUrl?: string;
  scale: [number, number, number];
  position: [number, number, number];
  occluderScale?: [number, number, number];
  occluderPosition?: [number, number, number];
};
type TargetRuntime = {
  config: TargetConfig;
  anchor: any;
  model: THREE.Object3D | null;
  occluder: THREE.Object3D | null;
  mixer: THREE.AnimationMixer | null;
  action: THREE.AnimationAction | null;
};

type InitARParams = {
  header: HTMLElement;
  stepIntroContainer: HTMLElement;
  imageTargetSrc: string;
  targetConfigs: TargetConfig[];
  errorDiv?: HTMLElement;
  onExperienceFinished?: (animationName: string) => void;
  onTargetMatched?: (targetName: string) => void;
};

export class ARExperience {
  private header: HTMLElement;
  private stepIntroContainer: HTMLElement;
  private imageTargetSrc: string;
  private targetConfigs: TargetConfig[];
  private errorDiv?: HTMLElement;
  private onExperienceFinished?: (animationName: string) => void;
  private onTargetMatched?: (targetName: string) => void;

  private introAdvanceHandler?: () => void;
  private introAdvanced = false;
  private introStarted = false;

  private mindarThree: any;
  private targets: TargetRuntime[] = [];
  private redirectTimeout: number | null = null;
  private loader = new GLTFLoader();

  constructor({
    header,
    stepIntroContainer,
    imageTargetSrc,
    targetConfigs,
    errorDiv,
    onExperienceFinished,
    onTargetMatched,
  }: InitARParams) {
    this.header = header;
    this.stepIntroContainer = stepIntroContainer;
    this.imageTargetSrc = imageTargetSrc;
    this.targetConfigs = targetConfigs;
    this.errorDiv = errorDiv;
    this.onExperienceFinished = onExperienceFinished;
    this.onTargetMatched = onTargetMatched;

    this.mindarThree = new MindARThree({
      container: document.querySelector('#container'),
      imageTargetSrc: this.imageTargetSrc,
      filterMinCF: 0.001,
      filterBeta: 0.001,
      warmupTolerance: 20,
      missTolerance: 5,
      autoStart: false,
      uiScanning: false,
    });
  }

  private cleanupIntroListeners() {
    if (this.introAdvanceHandler) {
      document.removeEventListener('touchstart', this.introAdvanceHandler);
      document.removeEventListener('click', this.introAdvanceHandler);
      this.introAdvanceHandler = undefined;
    }
  }

  private startIntroAdvance(configName: string) {
    if (this.introStarted) return;
    this.introStarted = true;

    this.stepIntroContainer.style.display = 'none';
    this.header.classList.add('hidden');

    this.introAdvanceHandler = () => {
      this.advanceIntro(configName);
    };

    document.addEventListener('touchstart', this.introAdvanceHandler, { once: true });
    document.addEventListener('click', this.introAdvanceHandler, { once: true });

    this.redirectTimeout = window.setTimeout(() => {
      this.advanceIntro(configName);
    }, 4000);
  }

  private advanceIntro(configName: string) {
    if (this.introAdvanced) return;
    this.introAdvanced = true;

    if (this.redirectTimeout) {
      window.clearTimeout(this.redirectTimeout);
      this.redirectTimeout = null;
    }

    this.cleanupIntroListeners();

    this.onExperienceFinished?.(configName);

    const front = document.getElementById('intro');
    front?.classList.remove('hidden');
    front?.classList.add('active');
  }

  private applyModelSettings(model: THREE.Object3D, config: TargetConfig) {
    model.visible = false;
    model.scale.set(...config.scale);
    model.position.set(...config.position);

    model.traverse((node) => {
      if ((node as THREE.Mesh).isMesh) {
        (node as THREE.Mesh).renderOrder = 2;
      }
    });
  }

  private disposeModel(model?: THREE.Object3D) {
    if (!model) return;

    model.traverse((node) => {
      const mesh = node as THREE.Mesh;
      if (!mesh.isMesh) return;

      mesh.geometry?.dispose();

      const material = mesh.material;
      if (Array.isArray(material)) {
        material.forEach((mat) => mat.dispose());
      } else {
        material?.dispose();
      }
    });
  }

  private loadModelIntoRuntime(runtime: TargetRuntime, modelUrl: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.loader.load(
        modelUrl,
        (glb) => {
          const model = glb.scene;
          this.applyModelSettings(model, runtime.config);

          runtime.model = model;
          runtime.mixer = new THREE.AnimationMixer(model);

          if (glb.animations.length > 0) {
            const clip = glb.animations[0];
            const action = runtime.mixer.clipAction(clip);

            const isIntro = runtime.config.name === 'intro';

            if (isIntro) {
              action.clampWhenFinished = true;
              action.loop = THREE.LoopOnce;
            } else {
              action.clampWhenFinished = false;
              action.setLoop(THREE.LoopRepeat, Infinity);
            }

            runtime.action = action;
          } else {
            runtime.action = null;
          }

          runtime.anchor.group.add(model);
          resolve();
        },
        undefined,
        (err) => {
          console.error(`Erro ao carregar modelo ${runtime.config.name}`, err);
          reject(err);
        }
      );
    });
  }
  async replaceModel(targetName: string, modelUrl: string) {
    const runtime = this.targets.find((target) => target.config.name === targetName);

    if (!runtime) {
      console.warn(`Target não encontrado para troca de modelo: ${targetName}`);
      return;
    }

    if (runtime.config.modelUrl === modelUrl) {
      console.log(`Modelo já está aplicado em ${targetName}`);
      return;
    }

    const previousModel: THREE.Object3D | null = runtime.model;
    const wasVisible = previousModel ? previousModel.visible : false;

    if (previousModel) {
      runtime.anchor.group.remove(previousModel);
      this.disposeModel(previousModel);
    }

    runtime.model = null;
    runtime.mixer = null;
    runtime.action = null;
    runtime.config.modelUrl = modelUrl;

    await this.loadModelIntoRuntime(runtime, modelUrl);

    const loadedModel = runtime.model as THREE.Object3D | null;
    const loadedAction = runtime.action as THREE.AnimationAction | null;

    if (loadedModel !== null) {
      loadedModel.visible = wasVisible;
    }

    if (wasVisible && loadedAction !== null) {
      loadedAction.reset();
      loadedAction.play();
    }

    console.log(`Modelo trocado com sucesso para ${targetName}:`, modelUrl);
  }

  async init() {
    const page = document.body.dataset.page || '';

    const { renderer, scene, camera } = this.mindarThree;
    camera.near = 0.001;

    try {
      await this.mindarThree.start();
      console.log('MindAR iniciou com sucesso');
    } catch (error) {
      console.error('Erro ao iniciar o MindARThree:', error);
      if (this.errorDiv) {
        this.errorDiv.style.display = 'flex';
      }
      return;
    }

    const ambientLight = new THREE.AmbientLight(0xffffff, 1);
    scene.add(ambientLight);
    ambientLight.position.set(0, 3, 3);

    for (const config of this.targetConfigs) {
      const anchor = this.mindarThree.addAnchor(config.index);

      const runtime: TargetRuntime = {
        config,
        anchor,
        model: null,
        occluder: null,
        mixer: null,
        action: null,
      };
      await this.loadModelIntoRuntime(runtime, config.modelUrl);

      if (config.occluderUrl) {
        this.loader.load(
          config.occluderUrl,
          (glb) => {
            const occluder = glb.scene;
            occluder.visible = false;

            const occluderScale = config.occluderScale ?? config.scale;
            const occluderPosition = config.occluderPosition ?? config.position;

            occluder.scale.set(...occluderScale);
            occluder.position.set(...occluderPosition);

            occluder.traverse((node) => {
              if (node instanceof THREE.Mesh) {
                const material = node.material as THREE.Material;
                material.colorWrite = false;
                node.renderOrder = 1;
              }
            });

            runtime.occluder = occluder;
            anchor.group.add(occluder);
          },
          undefined,
          (err) => {
            console.error(`Erro ao carregar occluder ${config.name}`, err);
          }
        );
      }

      anchor.onTargetFound = () => {
        console.log('Target detectado:', config.name, 'page:', page);

        if (runtime.occluder) {
          runtime.occluder.visible = true;
        }

        if (runtime.model) {
          runtime.model.visible = true;
        }

        if (runtime.action) {
          runtime.action.reset();
          runtime.action.play();
        }

        if (page === 'intro') {
          this.startIntroAdvance(config.name);
        } else {
          this.onTargetMatched?.(config.name);
          document.querySelector('.target-text')?.classList.add('hidden');
        }
      };

      anchor.onTargetLost = () => {
        console.log('Target perdido:', config.name);

        if (runtime.model) {
          runtime.model.visible = false;
        }

        if (runtime.occluder) {
          runtime.occluder.visible = false;
        }
      };

      this.targets.push(runtime);
    }

    const clock = new THREE.Clock();

    const animate = () => {
      requestAnimationFrame(animate);

      const delta = clock.getDelta();

      for (const target of this.targets) {
        target.mixer?.update(delta);
      }

      renderer.render(scene, camera);
    };

    animate();

    window.addEventListener('resize', () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    });

    renderer.setAnimationLoop(() => {
      renderer.render(scene, camera);
    });
  }

  async stop() {
    await this.mindarThree.stop();
    this.mindarThree.renderer.setAnimationLoop(null);
    this.cleanupIntroListeners();

    if (this.redirectTimeout) {
      window.clearTimeout(this.redirectTimeout);
      this.redirectTimeout = null;
    }
  }
}